import { parseConfig, createElementFromHTML, stringToBoolean, isMobile, getLazyLoadComponentObserver, handleize } from '@utilities/theme';

if(!customElements.get('product-card')) {
  /**
   * ProductCard custom element that represents a product card on the site
   * Handles rendering, interactions, and data management for product cards
   * @class
   * @extends {HTMLElement}
   */
  class ProductCard extends HTMLElement {
    /**
     * Creates an instance of ProductCard
     * Sets up initial properties and state
     * @constructor
     */
    constructor() {
      super();
      // disable gallery flag
      this.disableGallery = this.dataset.disableGallery ? stringToBoolean(this.dataset.disableGallery) : false;
      // rendering mode
      this.renderingMode = this.dataset.renderingMode || 'server-side';
      this.showSoldOutState = this.dataset.showSoldOutState ? stringToBoolean(this.dataset.showSoldOutState) : false;
      // default variant id
      this.defaultVariantId = this.dataset.defaultVariantId ? parseInt(this.dataset.defaultVariantId, 10) : null;
      // elements
      this.elements = {};
      this.templates = {};
      // atc on variant resolve flag
      this.atcOnVariantResolve = true;
      // promotion
      this.promotion = {};
      // variant
      this.variant = {};
      // slider
      this.slider = null;
      // selected swatch data
      this.selectedSwatchData = null;
      // selected swatch element
      this.selectedSwatchElement = null;
    }
    /**
     * Determines if lazy loading is enabled for this product card
     * @returns {boolean} Whether lazy loading is enabled
     */
    isLazyLoad(){
      // return true if the component has the lazy-load class
      return this.classList.contains("lazy-load");
    }

    /**
     * Determines if debugging is enabled for this product card
     * Returns true if the card has debug class or global debug is enabled
     * @returns {boolean} Whether debugging is enabled
     */
    allowDebug(){
      // return true if the component has the debug class
      return this.classList.contains("debug");
    }

    /**
     * Lifecycle callback when the element is added to the DOM
     * Either initializes the component or sets up lazy loading
     */
    async connectedCallback() {
      // lazy load flag
      if (this.isLazyLoad()) {
        // generate basic skeleton
        this.generateSkeleton();
        // observe the component for lazy loading
        getLazyLoadComponentObserver().observe(this);
      } else {
        // initialize the component
        await this.init();
      }
    }

    /**
     * Generates a basic skeleton for the product card
     */
    generateSkeleton(){
      // generate the skeleton
      this.skeleton = createElementFromHTML(`
        <div class="card-product-skeleton color-background-2">
          <div class="card-product-skeleton__media gradient">
            <div class="card-product-skeleton__media-image"></div>
          </div>
          <div class="card-product-skeleton__content">
            <div class="card-product-skeleton__title gradient"></div>
            <div class="card-product-skeleton__price gradient"></div>
          </div>
        </div>
      `);
      // insert the skeleton into the component
      this.appendChild(this.skeleton);
    }

    /**
     * Initializes the product card component
     * Sets up data, elements, event listeners, and renders content if needed
     */
    async init(){
      try {
        // allow debug flag
        if(this.allowDebug()) console.log('product-card.js: init()', this);
        // set product data
        this.product = await this.setProduct();
        // If product is null (fetch failed), the component should have been removed
        // Exit early to prevent further initialization
        if (!this.product) {
          if(this.allowDebug()) console.log('product-card.js: init() - Product data is null, component likely removed from DOM');
          return;
        }
        if(this.allowDebug()) console.log('product-card.js product:', this.product);
        // set default variant
        this.variant = this.setDefaultVariant();
        // If variant is null, the component should be removed (setDefaultVariant handles this)
        // Exit early to prevent further initialization
        if (!this.variant) {
          if(this.allowDebug()) console.log('product-card.js: init() - Variant is null, component likely removed from DOM');
          return;
        }
        if(this.allowDebug()) console.log('product-card.js variant:', this.variant);
        // set template elements HTML
        this.setTemplateHTML();
        // Check rendering mode and perform client-side rendering if needed
        if (this.renderingMode === 'client-side') {
          this.renderClientSide();
        }
        // remove the skeleton
        this.querySelector('.card-product-skeleton')?.remove();
        // set content element variables
        this.setContentElements();
        // set event listeners
        this.setEventListeners();
        // set default swatch data from the rendered card + variant
        this.setDefaultSwatchData();
        // generate the slider for the media, if we can figure out a clean way to do this we should really have the slider generate on mouseover.
        // for mobile, let's generate the slider.
        if(isMobile()){
          this.generateSwatchMediaSlider(this.selectedSwatchData);
          const firstSlider = this.querySelector('.first-slider.flickity-enabled');
          if(firstSlider){
            // delay adding the nudge class for a better user experience
            setTimeout(() => {
              firstSlider.querySelector('.flickity-slider').classList.add('nudge');
            }, 500);
          }
        }
        // inject quickshop
        this.injectQuickShop();
        // set observers
        this.setObservers();
        // add the initialized class
        this.classList.add('product-card--initialized');
        // log the initialization
        if(this.allowDebug()) console.log('product-card.js: init() finished.', this.product, this);
      } catch (error) {
        console.error('Error initializing component:', error, this);
        // If there's an error during initialization and we haven't already removed the component,
        // remove it from the DOM as a safety measure
        if (this.isConnected) {
          this.removeFromDOM();
        }
      }
    }

    /**
     * Sets the default variant for the product card
     * Prioritizes default variant ID from the product data
     * @returns {Object|null} The default variant object or null if not found
     */
    setDefaultVariant(){
      // Early exit conditions
      if(!this.product) {
        if(this.allowDebug()) console.error('product-card.js: setDefaultVariant() failed, no product found.', this);
        return null;
      }

      if (!this.product.available) {
        if(this.allowDebug()) {
          console.log('product-card.js: setDefaultVariant() - Product is not available, removing from DOM', this.product);
        }
        setTimeout(() => this.removeFromDOM(), 0);
        return null;
      }

      if (!this.product.variants || !Array.isArray(this.product.variants) || this.product.variants.length === 0) {
        if(this.allowDebug()) console.error('product-card.js: setDefaultVariant() failed, no variants available.', this.product);
        setTimeout(() => this.removeFromDOM(), 0);
        return null;
      }

      if(this.allowDebug()) {
        console.log('product-card.js: setDefaultVariant()', {
          defaultVariantId: this.defaultVariantId,
          defaultChildVariantId: this.product.default_child_variant_id,
          defaultVariantIdFromProduct: this.product.default_variant_id,
          isParentProduct: this.product.is_parent_product,
          variantsCount: this.product.variants.length
        });
      }

      // Priority 1: Check spotlight color variant selection first
      // This handles the case where products need to match collection spotlight colors.
      // Only applies when: collection context, no filters/sorting, spotlight configured.
      if (this.shouldUseSpotlightVariantSelection()) {
        if(this.allowDebug()) console.log(`product-card.js: setDefaultVariant() searching for spotlight color variant for: ${this.product.title}`);
        const spotlightVariant = this.findSpotlightVariant();
        if (spotlightVariant) {
          if(this.allowDebug()) console.log('product-card.js: setDefaultVariant() using spotlight color variant:', spotlightVariant);
          return spotlightVariant;
        }
      }

      // Priority 2: For parent products, use default_child_variant_id (fallback after spotlight check)
      if(this.product.is_parent_product && this.product.default_child_variant_id) {
        const childVariant = this.findVariantById(this.product.default_child_variant_id);
        if (childVariant) {
          if(this.allowDebug()) console.log('product-card.js: setDefaultVariant() using default_child_variant_id:', childVariant);
          return childVariant;
        }
      }

      // Priority 3: Use data-default-variant-id from HTML dataset (filter-aware selection from ProductGrid)
      if(this.defaultVariantId) {
        const datasetVariant = this.findVariantById(this.defaultVariantId);
        if (datasetVariant) {
          if(this.allowDebug()) console.log('product-card.js: setDefaultVariant() using filter-aware defaultVariantId:', datasetVariant);
          return datasetVariant;
        }
      }

      // Priority 4: Use product's default_variant_id
      if(this.product.default_variant_id) {
        const productDefaultVariant = this.findVariantById(this.product.default_variant_id);
        if (productDefaultVariant) {
          if(this.allowDebug()) console.log('product-card.js: setDefaultVariant() using product default_variant_id:', productDefaultVariant);
          return productDefaultVariant;
        }
      }

      // Priority 5: Find first available variant (prefer fully available, then shopify available)
      let availableVariant = this.product.variants.find(variant => this.isVariantFullyAvailable(variant));
      if (availableVariant) {
        if(this.allowDebug()) console.log('product-card.js: setDefaultVariant() using first fully available variant:', availableVariant);
        return availableVariant;
      }

      availableVariant = this.product.variants.find(variant => this.isVariantShopifyAvailable(variant));
      if (availableVariant) {
        if(this.allowDebug()) console.log('product-card.js: setDefaultVariant() using first shopify available variant:', availableVariant);
        return availableVariant;
      }

      // Final fallback: First variant in array
      const fallbackVariant = this.product.variants[0];
      if(this.allowDebug()) {
        console.log('product-card.js: setDefaultVariant() using first variant as final fallback:', fallbackVariant);
      }
      return fallbackVariant;
    }



    /**
     * Fetches and parses the product data from the JSON script tag
     * @returns {Object} The parsed product data
     */
    async setProduct(){
      // if we are not rendering client-side, return the product data from the JSON script tag
      // if we are rendering client-side, the product will be fetched from the url stored in the data-fetch-url attribute, this url will return a JSON string with the product data that must be parsed.
      if(this.renderingMode !== 'client-side') {
        const productData = parseConfig(this);
        return productData;
      } else {
        // return the product data
        return await this.fetchProduct();
      }
    }

    /**
     * Fetches and parses the product data from the JSON script tag
     * @returns {Object} The parsed product data
     */
    async fetchProduct(){
      try {
        const response = await fetch(this.dataset.fetchUrl);

        // Check if the response is ok (status 200-299)
        if (!response.ok) {
          if (response.status === 404) {
            console.warn(`Product not found (404): ${this.dataset.fetchUrl}. Removing product card from DOM.`, this);
          } else {
            console.warn(`Failed to fetch product (${response.status}): ${this.dataset.fetchUrl}. Removing product card from DOM.`, this);
          }

          // Remove the component from the DOM
          this.removeFromDOM();
          return null;
        }

        const data = await response.json();

        // Check if this is a child product and handle replacement with parent
        if (data && data.is_child_product && data.default_parent_product_url) {
          if(this.allowDebug()) {
            console.log('product-card.js: fetchProduct() - Child product detected, replacing with parent product', data);
          }
          return await this.replaceChildWithParentProduct(data);
        }

        return data;

      } catch (error) {
        console.warn(`Error fetching product data: ${error.message}. Removing product card from DOM.`, this);
        // Remove the component from the DOM
        this.removeFromDOM();
        return null;
      }
    }

    /**
     * Replaces a child product with its parent product for proper filtering support
     * Updates the current card's dataset values and fetches parent product data
     * @param {Object} childProductData - The child product data
     * @returns {Object|null} The parent product data or null if replacement fails
     */
    async replaceChildWithParentProduct(childProductData) {
      try {
        if(this.allowDebug()) {
          console.log('product-card.js: replaceChildWithParentProduct() - Starting replacement process', childProductData);
        }

        // find the variants entry in childProductData.variants where the id is equal to childProductData.default_variant_id
        const childProductVariant = childProductData.variants.find(variant => variant.id === childProductData.default_variant_id);
        const parentFetchUrl = `${childProductVariant.url}&view=product-json`;

        // Update dataset values to point to parent product
        this.dataset.fetchUrl = parentFetchUrl;
        this.dataset.productUrl = childProductVariant.url;

        // Set the default variant ID - prefer the child variant if available
        if (childProductVariant.parent_variant_id) {
          this.dataset.defaultVariantId = childProductVariant.parent_variant_id;
          this.defaultVariantId = parseInt(childProductVariant.parent_variant_id, 10);
        }

        // Mark this as a parent product card that was converted from a child
        this.classList.add('parent-product-card');
        this.classList.add('converted-from-child');

        // Add debug class for debugging
        if(this.allowDebug()) {
          this.classList.add('debug');
        }

        if(this.allowDebug()) {
          console.log('product-card.js: replaceChildWithParentProduct() - Dataset updated, fetching parent product data', {
            fetchUrl: this.dataset.fetchUrl,
            productUrl: this.dataset.productUrl,
            defaultVariantId: this.dataset.defaultVariantId
          });
        }

        // Fetch the parent product data directly instead of calling init() recursively
        const parentResponse = await fetch(parentFetchUrl);

        if (!parentResponse.ok) {
          throw new Error(`Failed to fetch parent product: ${parentResponse.status}`);
        }

        const parentProductData = await parentResponse.json();

        if(this.allowDebug()) {
          console.log('product-card.js: replaceChildWithParentProduct() - Successfully fetched parent product data', parentProductData);
        }

        // Simply use the parent product data - let ProductGrid handle filter logic
        if(this.allowDebug()) {
          console.log('product-card.js: replaceChildWithParentProduct() - Using parent product data');
        }

        // Return the parent product data so the original init can continue
        return parentProductData;

      } catch (error) {
        console.error('product-card.js: replaceChildWithParentProduct() - Failed to replace child with parent:', error);
        // If replacement fails, proceed with child product data
        return childProductData;
      }
    }

    /**
     * Removes the product card from the DOM
     * If there's a parent .grid__item element, removes that instead
     */
    removeFromDOM() {
      try {
        // Look for parent grid item first
        const parentGridItem = this.closest('.grid__item');

        if (parentGridItem) {
          if (this.allowDebug()) {
            console.log('product-card.js: removeFromDOM() - Removing parent grid item', parentGridItem);
          }
          parentGridItem.remove();
        } else {
          if (this.allowDebug()) {
            console.log('product-card.js: removeFromDOM() - Removing product card directly', this);
          }
          this.remove();
        }
      } catch (error) {
        console.error('Error removing product card from DOM:', error, this);
      }
    }

    /**
     * Finds and stores references to template elements
     * These templates are used for rendering parts of the component
     */
    setTemplateHTML(){
      // Early exit if variant is not available
      if (!this.variant) {
        if(this.allowDebug()) {
          console.log('product-card.js: setTemplateHTML() - No variant available, skipping template setup');
        }

        // Set minimal templates to prevent errors
        this.templates.quickViewTemplateHTML = '';
        this.templates.quickAddToBagTemplateHTML = '';
        this.templates.promotionTemplateHTML = '';
        this.templates.componentTemplateHTML = `
          <div class="card-wrapper product-card-wrapper">
            <div class="card card--standard card--media">
              <div class="card__inner color-background-2 gradient ratio">
                <div class="card__media"></div>
              </div>
              <div class="card__content">
                <div class="card__information"></div>
              </div>
            </div>
          </div>
        `;
        return;
      }

      // get the quick view template
      this.templates.quickViewTemplateHTML = `
        <div class="quick-shop-container">
          <div class="quick-add no-js-hidden mobile-hidden">
            <modal-opener data-modal="#QuickAdd-${this.product.id}">
              <button
                id="${this.dataset.productFormId}-submit"
                type="submit"
                name="add"
                class="quick-add__submit"
                aria-haspopup="dialog"
                aria-labelledby="${this.dataset.productFormId}-submit title-${this.dataset.sectionId}-${this.product.id}"
                data-product-url="${this.product.url || '#'}"
              >
                ${window.theme.language_strings.product_quick_view}
                <div class="loading-overlay__spinner hidden">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    class="spinner"
                    viewBox="0 0 66 66"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                  </svg>
                </div>
              </button>
            </modal-opener>
            <quick-add-modal id="QuickAdd-${this.product.id}" class="quick-add-modal ${this.allowDebug() ? 'debug' : ''}">
              <span class="modal-overlay"></span>
              <div
                role="dialog"
                aria-label="${window.theme.language_strings.product_quick_view_view_details}"
                aria-modal="true"
                class="quick-add-modal__content global-settings-popup"
                tabindex="-1"
              >
                <button
                  id="ModalClose-${this.product.id}"
                  type="button"
                  class="quick-add-modal__toggle"
                  aria-label="${window.theme.language_strings.accessibility_close}"
                >
                  ${window.theme.elements.svg_close}
                </button>
                <div id="QuickAddInfo-${this.product.id}" class="quick-add-modal__content-info"></div>
              </div>
            </quick-add-modal>
          </div>
        </div>
      `;
      // get the quick add to bag template
      this.templates.quickAddToBagTemplateHTML = `
        <div class="quick-shop-container">
          <div class="quick-add no-js-hidden mobile-hidden">
            <quick-add-to-bag
              class="quick-add-to-bag ${this.allowDebug() ? 'debug' : ''}"
            >
              <button
                id="${this.dataset.productFormId}-trigger"
                type="button"
                class="quick-add-to-bag__trigger"
                data-product-url="${this.product.url || '#'}"
              >
                Add To Bag
                <div class="loading-overlay__spinner hidden">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    class="spinner"
                    viewBox="0 0 66 66"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                  </svg>
                </div>
              </button>
              <div class="quick-add-to-bag__option-select-container"></div>
              <div class="quick-add-to-bag__cart-messages"></div>
            </quick-add-to-bag>
          </div>
        </div>
      `;
      // set the promotion template
      this.templates.promotionTemplateHTML = `
        <product-promotion
          class="card__promotion lazy-load"
          id="product__promotion--${this.product.handle}"
        >
          <script type="application/json" id="product__promotion--${this.product.handle}__json" component-config>
            {
              "template": "card",
              "fetch": {
                "url": "${this.variant.url || this.product.url || '#'}"
                ${this.product.is_parent_product ? `
                  ,"backup": {
                    "url": "${this.product.default_child_variant_url || '#'}"
                  }
                ` : ''}
              }
            }
          </script>
          <div class="product__promotion__inner"></div>
        </product-promotion>
      `;
      // set the component base template
      this.templates.componentTemplateHTML = `
        <div class="card-wrapper product-card-wrapper">
          <div class="card card--standard card--media">
            <div class="card__inner color-background-2 gradient ratio">
              <div class="card__media"></div>
            </div>
            <div class="card__content">
              <div class="card__information"></div>
              ${this.product.block_promotions ? '' : this.templates.promotionTemplateHTML}
            </div>
          </div>
        </div>
      `;
    }

    /**
     * Finds and stores references to content elements
     * These elements contain the visible content of the component
     */
    setContentElements(){
      // get the price element
      this.elements.priceElement = this.querySelector('.card-price-container .card-price-container__price-option');
      // get the product promotion
      this.elements.productPromotion = this.querySelector('product-promotion');
      // get the product swatches
      this.elements.productSwatches = this.querySelector('.product-card-swatches__container');
    }

    /**
     * Renders the product card content on the client side
     * Used when client-side rendering mode is enabled
     */
    renderClientSide() {
      if(this.allowDebug()) console.log('product-card.js: renderClientSide()', this.product);
      // early exit conditions
      if (!this.product || !this.templates.componentTemplateHTML) {
        console.error('Missing data or client template for client-side rendering', this);
        return;
      }
      // try to render the client side
      try {
        // clear the existing content
        this.replaceChildren();
        // clone the template content into a new element
        const templateHTML = createElementFromHTML(this.templates.componentTemplateHTML);
        // render the card media
        this.renderClientSideCardMedia(templateHTML);
        // render the card media badges
        this.renderClientSideCardMediaBadges(templateHTML);
        // render the card information
        this.renderClientSideCardTitle(templateHTML);
        // render the card price
        this.renderClientSideCardPrice(templateHTML);
        // render the card info badges
        this.renderClientSideCardInfoBadges(templateHTML);
        // render the card swatches
        this.renderClientSideCardSwatches(templateHTML);
        // insert the rendered template into the component
        this.appendChild(templateHTML);

        if(this.allowDebug()) console.log('product-card.js: renderClientSide() finished', templateHTML);
      } catch (error) {
        console.error('Error rendering client-side:', error, this);
      }
    }

    /**
     * Renders the media overlaybadges portion of the product card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardMediaBadges(container){
      const mediaBadgesContainer = container.querySelector('.card__inner');
      if(!mediaBadgesContainer) return;

      // Render membership messaging if applicable
      const productMembership = this.product.membership || null;
      const productMembershipRequired = productMembership?.membership_required || false;
      const productMembershipGroup = productMembership?.membership_group_required || null;
      const productMembershipGroupHandle = productMembershipRequired ?
        String(handleize(productMembershipGroup?.title) || 'default').replace('-', '_') : '';

      if(productMembershipGroupHandle.length > 0){
        const membershipBadgeTemplateHTML = `
          <div class="members-messaging">
            <div class="members-messaging--icon">
              ${window.theme.elements.membership_icons[productMembershipGroupHandle]}
            </div>
          </div>
        `;
        mediaBadgesContainer.appendChild(createElementFromHTML(membershipBadgeTemplateHTML));
      }

      // Render filtered badges for image label location
      this.renderBadges(mediaBadgesContainer, "Image label", "image__badges");
    }

    /**
     * Gets collection data from DOM instead of making fetch calls
     * @returns {Object|null} The collection data or null if not found
     */
    getProductGridConfig() {
      const closestProductGrid = this.closest('product-grid');
      return closestProductGrid?.config || null;
    }

    /**
     * Check if ProductGrid is filtered or sorted
     * @returns {boolean} True if ProductGrid has data-filtered="true" or product-grid--filtered class
     * @private
     */
    isProductGridFiltered() {
      const productGrid = this.closest('product-grid');
      if (!productGrid) return false;

      return (productGrid.hasAttribute('data-filtered') &&
              productGrid.getAttribute('data-filtered') === 'true') ||
             productGrid.classList.contains('product-grid--filtered');
    }

    /**
     * Check if ProductGrid is price sorted
     * @returns {boolean} True if ProductGrid is sorted by price
     * @private
     */
    isProductGridPriceSorted() {
      const productGridConfig = this.getProductGridConfig();
      if (!productGridConfig) return false;

      return productGridConfig.sortBy === 'price-ascending' ||
             productGridConfig.sortBy === 'price-descending';
    }

    /**
     * Determine if spotlight variant selection should be used
     * Only applies when:
     * - ProductGrid exists and context is "collection"
     * - ProductGrid is NOT filtered or price sorted
     * - Spotlight colors are configured
     * @returns {boolean} True if spotlight variant selection should be used
     * @private
     */
    shouldUseSpotlightVariantSelection() {
      const productGridConfig = this.getProductGridConfig();

      // Must have ProductGrid config
      if (!productGridConfig) return false;

      // Must be collection context
      if (productGridConfig.context !== 'collection') return false;

      // Must NOT be filtered or price sorted
      if (this.isProductGridFiltered() || this.isProductGridPriceSorted()) return false;


      // Must have spotlight colors configured
      const hasSpotlightColors = productGridConfig.settings?.colorSpotlightColors &&
                                 Array.isArray(productGridConfig.settings.colorSpotlightColors) &&
                                 productGridConfig.settings.colorSpotlightColors.length > 0;
      const hasSpotlightColorFamily = productGridConfig.settings?.colorSpotlightColorFamily;

      if (!hasSpotlightColors && !hasSpotlightColorFamily) return false;

      return true;
    }

    /**
     * Find variant matching spotlight colors
     *
     * This is a "delay layer" to handle the case where products need to match collection
     * spotlight colors. Since external services may only provide product IDs, we can't properly
     * select the correct variant matching the spotlight until we fetch comprehensive product
     * data from Shopify.
     *
     * This only applies when:
     * - Collection context (not search)
     * - No filters or sorting active
     * - Spotlight colors configured
     *
     * Performance: Products will have a slightly longer load time, but this should
     * not be perceivable as it happens after the initial product data fetch.
     *
     * @returns {Object|null} The matching variant or null if not found
     * @private
     */
    findSpotlightVariant() {
      const productGridConfig = this.getProductGridConfig();
      if (!productGridConfig || !this.product?.variants) return null;

      const spotlightColors = productGridConfig.settings?.colorSpotlightColors;
      const spotlightColorFamily = productGridConfig.settings?.colorSpotlightColorFamily;

      // Normalize spotlight colors using handleize (matching Nosto utilities approach)
      const normalizedSpotlightColors = Array.isArray(spotlightColors) && spotlightColors.length > 0
        ? spotlightColors.map(color => handleize(color))
        : [];

      // First priority: Match by variant color property (normalized) against spotlight colors array
      if (normalizedSpotlightColors.length > 0) {
        for (const spotlightColor of normalizedSpotlightColors) {
          for (const variant of this.product.variants) {
            // Only consider variants that are greyson available
            if (!variant.available_greyson) continue;

            // Normalize variant color and check for match
            const normalizedVariantColor = variant.color ? handleize(variant.color) : null;
            if (normalizedVariantColor === spotlightColor) {
              if (this.allowDebug()) {
                console.log('product-card.js: findSpotlightVariant() found match by color:', {
                  spotlightColor,
                  variantColor: variant.color,
                  variantId: variant.id
                });
              }
              return variant;
            }
          }
        }
      }

      // Second priority: Match by variant color_family property against colorSpotlightColorFamily
      if (spotlightColorFamily) {
        for (const variant of this.product.variants) {
          // Only consider variants that are greyson available
          if (!variant.available_greyson) continue;

          // Check for color family match
          if (handleize(variant.color_family) === handleize(spotlightColorFamily)) {
            if (this.allowDebug()) {
              console.log('product-card.js: findSpotlightVariant() found match by color_family:', {
                spotlightColorFamily,
                variantColorFamily: variant.color_family,
                variantId: variant.id
              });
            }
            return variant;
          }
        }
      }

      if (this.allowDebug()) {
        console.log('product-card.js: findSpotlightVariant() no matching spotlight variant found');
      }

      return null;
    }

    /**
     * Checks if a badge should be shown based on collection filtering rules
     * @param {Object} badge - The badge object to check
     * @param {Object} productGridConfig - The product grid config
     * @returns {boolean} Whether the badge should be shown
     */
    shouldShowBadge(badge, productGridConfig) {
      if (!productGridConfig) return true;

      const rejectedBadges = productGridConfig.rejected_badges || [];
      const exclusiveBadges = productGridConfig.exclusive_badges || [];

      // Check if badge is rejected by collection
      const isRejectedByCollection = Array.isArray(rejectedBadges) &&
        rejectedBadges.some(rejectedBadge => rejectedBadge.label === badge.label);

      // Check if badge is exclusive by collection
      const isExclusiveByCollection = Array.isArray(exclusiveBadges) &&
        exclusiveBadges.some(exclusiveBadge => exclusiveBadge.label === badge.label);

      // If rejected by collection, don't show
      if (isRejectedByCollection) return false;

      // If exclusive by collection but not in exclusive list, don't show
      if (exclusiveBadges.length > 0 && !isExclusiveByCollection) return false;

      // Check badge's exclusive collections
      if (badge.exclusive_collections?.length > 0) {
        if (!badge.exclusive_collections.includes(Number(productGridConfig.id))) {
          return false;
        }
      }

      // Check badge's rejected collections
      if (badge.rejected_collections?.length > 0) {
        if (badge.rejected_collections.includes(Number(productGridConfig.id))) {
          return false;
        }
      }

      return true;
    }

    /**
     * Filters badges based on location and collection rules
     * @param {string} targetLocation - The target location ("Image label" or "Card info")
     * @returns {string} HTML string of filtered badges
     */
    getFilteredBadgesHTML(targetLocation) {
      if (this.showSoldOutState && targetLocation == "Image label") {
        return `
          <span
            class="badge badge-new"
            style="
              background-color: ${window.theme.colors.sold_out_badge_background_color};
              color: ${window.theme.colors.sold_out_badge_text_color}
            "
          >
            ${window.theme.language_strings.variant_sold_out}
          </span>
        `;
      }

      const productBadges = this.product.badges || [];
      if (productBadges.length === 0) return '';

      const productGridConfig = this.getProductGridConfig();

      return productBadges
        .filter(badge => {
          const shouldShow = this.shouldShowBadge(badge, productGridConfig);
          const badgeLocation = badge.card_location || "Image label";
          return shouldShow && badgeLocation === targetLocation;
        })
        .map(badge => badge.html)
        .join('');
    }

    /**
     * Renders the media (images) portion of the product card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardMedia(container) {
      // get the media container
      const mediaContainer = container.querySelector('.card__media');
      // early exit conditions
      if (!mediaContainer) return;
      if(this.allowDebug()) console.log('product-card.js: renderClientSideCardMedia()', this.product);

      // Get media from data
      // primary product card media is stored in the swatch array
      // search for a swatch where the color name matches the variant color
      // the swatch's media array is a string array of image tag HTML
      let media = [];

      if (this.variant && this.variant.color) {
        media = this.product.swatches?.find((swatch) => swatch.color == this.variant.color)?.media || [];
      }

      // if we cannot find swatch media, use the product media
      if(!media || media.length === 0){
        media = this.product.media || [];
      }
      if (media && media.length > 0) {
        // clear the media container
        mediaContainer.replaceChildren();
        const firstMedia = media[0].replace('loading="lazy"', '');
        let mediaContainerHTML = `
          <a
            id="product-card-media-link-${this.product.handle}"
            href="${(this.variant && this.variant.url) || this.product.url || '#'}"
            class="media media--transparent ${media.length > 1 ? 'media--hover-effect' : ''}"
          >
            ${firstMedia}
            ${media.length > 1 ? media[1] : ''}
          </a>
        `;
        // insert the link node into the media container
        mediaContainer.appendChild(createElementFromHTML(mediaContainerHTML));
      }
    }
    /**
     * Renders badges in a specific container with location filtering
     * @param {HTMLElement} container - The container element to render into
     * @param {string} targetLocation - The target location ("Image label" or "Card info")
     * @param {string} containerClass - CSS class for the badge container
     */
    renderBadges(container, targetLocation, containerClass) {
      const badgeHTML = this.getFilteredBadgesHTML(targetLocation);
      if(badgeHTML.length > 0){
        const badgeContainerHTML = `<div class="${containerClass}">${badgeHTML}</div>`;
        const badgeElement = createElementFromHTML(badgeContainerHTML);
        // Insert as first child to ensure badges appear at the top
        // If no children exist, appendChild will be used automatically
        if (container.firstChild) {
          container.insertBefore(badgeElement, container.firstChild);
        } else {
          container.appendChild(badgeElement);
        }
      }
    }

    /**
     * Renders the product info badges portion of the card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardInfoBadges(container){
      const infoBadgesContainer = container.querySelectorAll('.card__information')[0];
      if (!infoBadgesContainer) return;
      this.renderBadges(infoBadgesContainer, "Card info", "card__badges");
    }

    /**
     * Renders the product title portion of the card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardTitle(container) {
      // get the info container
      const cardTitleContainer = container.querySelector('.card__information');
      // early exit conditions
      if (!cardTitleContainer) return;

      const titleHTML = `
        <h3 class="card__heading h5">
          <a
            id="product-title-link-${this.product.handle}"
            href="${this.variant.url || '#'}"
            class="full-unstyled-link"
          >
            ${this.product.title_html || this.product.title || 'Product'}
          </a>
        </h3>
      `;
      // insert the title into the info container
      cardTitleContainer.appendChild(createElementFromHTML(titleHTML));
      // product subtitle, if applicable
      const productSubtitle = this.product.subtitle;
      if(productSubtitle){
        const subtitleHTML = `
          <p class="card__subtitle">${productSubtitle}</p>
        `;
        cardTitleContainer.appendChild(createElementFromHTML(subtitleHTML));
      }
    }

    /**
     * Renders the price portion of the product card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardPrice(container) {
      // get the info container, price element is a child of the info container
      const priceContainer = container.querySelector('.card__information');
      // early exit conditions
      if (!priceContainer) return;
      const priceInnerHTML = `<div class="card-price-container__price-option">${this.variant.price_html}</div><div class="card-price-container__price-range">${this.product.price_range_html}</div>`;
      const priceHTML = this.showSoldOutState ? `<span class="price--sold-out">${window.theme.language_strings.variant_sold_out}</span>` : priceInnerHTML;
      const priceContainerHTML = `
        <div class="card-information">
          <div class="card-price-container">
            ${priceHTML}
          </div>
        </div>
      `;
      priceContainer.appendChild(createElementFromHTML(priceContainerHTML));
    }

    /**
     * Renders the color swatches portion of the product card
     * @param {HTMLElement} container - The container element to render into
     */
    renderClientSideCardSwatches(container) {
      // get the swatches container
      const cardContent = container.querySelector('.card__content');
      // early exit conditions
      if (!cardContent || !this.product.swatches || !this.product.swatches.length || !this.product.available_colors.length) return;

      // maximum number of swatches to display
      const maxSwatches = 3;

      // Find current variant's swatch
      const currentVariantColor = this.variant.color;
      const currentVariantSwatch = this.product.swatches.find(swatch =>
        swatch.color === currentVariantColor && this.product.available_colors.includes(swatch.color)
      );

      // Create a prioritized list of swatches
      // 1. Start with the current variant's swatch
      // 2. Add other available swatches until we reach maxSwatches
      let swatchesToRender = [];

      // Add current variant swatch first, if it exists
      if (currentVariantSwatch) {
        swatchesToRender.push(currentVariantSwatch);
      }

      // Add other available swatches until we reach maxSwatches
      if (this.product.swatches.length > 1) {
        this.product.swatches.forEach(swatch => {
          // Only add different swatches that are available and not already included
          if (
            ((currentVariantSwatch && swatch.color !== currentVariantSwatch.color) || !currentVariantSwatch) && this.product.available_colors.includes(swatch.color) && swatchesToRender.length < maxSwatches) {
            swatchesToRender.push(swatch);
          }
        });
      }

      // Only continue if we have swatches to render
      if (swatchesToRender.length === 0) return;

      // start the swatches container html
      let swatchesContainerHTML = `
        <div class="product-card-swatches__container">
          <ul
            class="product-card-swatches product-card__option product-card__options--${handleize(this.product.color_option_name)}"
            data-option="${this.product.color_option_name}"
            data-option-key="${this.product.color_option_key}"
          >
      `;

      // Render each swatch in our prioritized order
      swatchesToRender.forEach((swatch, index) => {
        swatchesContainerHTML += `
          <li style="--swatch-image: url('${swatch.swatch_image}');--swatch-image-aspect-ratio: ${swatch.swatch_image_aspect_ratio};">
            <div
              class="product-card-swatch swatch--${swatch.handle}"
            >
              <input
                type="radio"
                id="product-${this.product.handle}-color-${swatch.handle}"
                name="color[${this.product.handle}]"
                value="${swatch.color}"
                class="product__option-input product__option-swatch"
                data-color="${swatch.handle}"
                data-option-key="${this.product.color_option_key}"
                ${index === 0 ? 'checked' : ''}
              >
              <label
                for="product-${this.product.handle}-color-${swatch.handle}"
                class="product__option-label"
              >
                <span class="visually-hidden">${swatch.color}</span>
              </label>
            </div>
          </li>
        `;
      });

      // Add "more colors" link if we have more swatches than the max
      if (this.product.available_colors.length > maxSwatches) {
        swatchesContainerHTML += `
          <li class="product-card-swatches-link-more">
            <a
              id="product-more-colors-link-${this.product.handle}"
              href="${this.variant.url}"
              class="link full-unstyled-link"
            >
              ${window.theme.language_strings.product_swatches_more_colors}
            </a>
          </li>
        `;
      }

      // close the swatches container
      swatchesContainerHTML += `
          </ul>
        </div>
      `;

      // insert the swatches container into the container
      cardContent.appendChild(createElementFromHTML(swatchesContainerHTML));

      // Log information if debug is enabled
      if (this.allowDebug()) {
        console.log('product-card.js: renderClientSideCardSwatches(), Rendered swatches:', swatchesToRender.map(s => s.color), this);
        console.log('product-card.js: renderClientSideCardSwatches(), Current variant color:', currentVariantColor, this);
      }
    }

    /**
     * Sets up event listeners for the product card
     * Uses event delegation for efficiency
     */
    setEventListeners(){
      // event delegation via clicks

      this.addEventListener('click', (event) => {
        // swatch click
        const { target } = event;
        // find the grid item
        const gridItem = target.closest("li.grid__item");
        // remove the nudge class if present
        if (gridItem?.classList.contains("nudge")) gridItem.classList.remove("nudge");
        // handle swatch click
        if(this.hasSwatches()){
          if (event.target && event.target.matches("input.product__option-input.product__option-swatch")) {
            this.handleProductSwatchClick(event.target);
          }
        }
        // option click
        if (event.target && event.target.matches("input.product__option-input")) {
          this.handleProductOptionClick(event.target);
        }
        // add to cart click
        if (event.target && event.target.matches("button.product-card__add-to-bag")) {
          this.handleAddToCartClick(event.target);
        }
        // Track product link clicks for Nosto analytics
        // See: https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
        const productLink = target.closest('a[href*="/products/"]');
        // Use dataset.productId as fallback for client-side rendered cards where this.product may not be loaded yet
        const hasProductId = this.product?.id || this.dataset.productId;
        if (productLink && this.dataset.vendor === "nosto" && hasProductId) {
          this.handleNostoProductClick();
        }
      });
      // handle swatch change
      this.addEventListener('product-card:swatch-change', this.handleSwatchChange);
      // handle promotion change
      this.addEventListener('product-promotion:change', this.handlePromotionChange);
      // handle mouseover on card_inner
      const cardInner = this.querySelector('.card__inner');
      if(cardInner){
        cardInner.addEventListener('mouseover', (event) => {
          if(!this.slider){
            this.generateSwatchMediaSlider(this.selectedSwatchData);
          }
        });
      }
    }

    /**
     * Sets up mutation observers to watch for changes to the component
     * Particularly watches for class changes that affect quick shop functionality
     */
    setObservers() {
      // Track the initial class state for comparison.
      let previousClassList = [...this.classList];
      // class change observer
      const classChangeObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === "attributes" && mutation.attributeName === "class") {
            const currentClassList = [...this.classList];
            const addedClasses = currentClassList.filter(cls => !previousClassList.includes(cls));
            const removedClasses = previousClassList.filter(cls => !currentClassList.includes(cls));

            // Determine if specific classes that trigger quick shop changes have been modified.
            const quickShopChange = [...addedClasses, ...removedClasses].some(cls => cls === "with__quickview" || cls === "with__add-to-bag");

            // React to changes related to quickshop functionality.
            if (quickShopChange) {
              if(this.allowDebug()) console.log('product-card.js: classChangeObserver(), a change in quickshop classes has been detected.', this);
              this.injectQuickShop();
            }

            if(addedClasses.includes("debug")){
              if(this.allowDebug()) console.log('product-card.js: classChangeObserver(), debug class has been added for the card.', this);
            }

            // Update the class list reference.
            previousClassList = currentClassList;
          }
        });
      });

      classChangeObserver.observe(this, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    /**
     * Injects the appropriate quick shop interface based on component classes
     * Either quick view or quick add to bag functionality
     */
    injectQuickShop() {
      // early exit conditions
      if(this.product.is_bundle_product) return;
      if(this.showSoldOutState) return;
      // quick shop container is a sibling of .card__media
      const cardMedia = this.querySelector('.card__media');
      if(!cardMedia) return;

      // Check for presence of quickshop classes.
      const hasQuickView = this.classList.contains("with__quickview");
      const hasAddToBag = this.classList.contains("with__add-to-bag");

      // Just in case, clear the quickshop container.
      // search for .quick-shop-container and remove it
      const quickShopContainer = this.querySelector('.quick-shop-container');
      if(quickShopContainer){
        quickShopContainer.remove();
      }

      // Inject quickshop action code on the present classes.
      try {
        // inject the quick view
        if (hasQuickView) {
          const content = createElementFromHTML(this.templates.quickViewTemplateHTML);
          // insert the new quick shop container after .card__media
          cardMedia.after(content);
        } else if (hasAddToBag) {
          // inject the quick add to bag
          const content = createElementFromHTML(this.templates.quickAddToBagTemplateHTML);
          // insert the new quick shop container after .card__media
          cardMedia.after(content);
        }
      } catch (error) {
        console.error("Error injecting quick shop content:", error, this);
      }
    }

    /**
     * Sets default swatch data when there's only one swatch option
     * Simulates a user click on the first swatch
     */
    setDefaultSwatchData() {
      if(this.allowDebug()) console.log('product-card.js: setDefaultSwatchData()', this);
      // if we do not have a selected swatch, get the first swatch
      let selectedSwatch = this.querySelector(".product__option-swatch:checked");
      if(!selectedSwatch){
        selectedSwatch = this.elements.productSwatches?.querySelector(".product__option-swatch:first-of-type");
      }
      // set the selected swatch data, if the element is null, the function sets empty values.
      this.setSelectedSwatchData(selectedSwatch);
    }

    /**
     * Handles promotion change events from the promotion component
     * @param {CustomEvent} event - The promotion change event
     */
    handlePromotionChange(event){
      // early exit conditions
      if (this.contains(event.detail.triggeringElement)) {
        this.promotion = event.detail.promotion;
      }
    }

    /**
     * Updates promotion variables when the variant changes
     * @param {Object} variant - The product variant object
     */
    async updatePromotionVariables(variant) {
      if (!variant || !this.elements.productPromotion || this.elements.productPromotion.lazyLoad) return;

      const promotionFetchData = {
        url: variant.url
      };

      if (this.product.is_parent_product) {
        promotionFetchData.url = variant.parent_variant_url;
        promotionFetchData.backup = {
          url: variant.child_variant_url
        };
      }

      await this.elements.productPromotion.updateFetchData(promotionFetchData);
    }

    /**
     * Handles product swatch click events
     * Updates the selected swatch data and dispatches a swatch change event
     * @param {HTMLElement} swatch - The swatch input element that was clicked
     */
    handleProductSwatchClick(swatchElement){
      // early exit conditions
      if(!swatchElement) return;
      // set the selected swatch dataset
      this.setSelectedSwatchData(swatchElement);
      // dispatch swatch change event
      this.dispatchEvent(new CustomEvent('product-card:swatch-change', {
        bubbles: true,
        detail: { triggeringElement: this, swatchData: this.selectedSwatchData, swatchElement: this.selectedSwatchElement }
      }));
    }

    /**
     * Sets the selected swatch data and element
     * @param {Object} swatch - The swatch data object
     */
    setSelectedSwatchData(swatchElement){
      // Find the matching swatch object based on data-color attribute
      const handleizedColor = swatchElement?.dataset?.color || '';
      // find the matching swatch
      let matchingSwatch = this.product?.swatches?.find((swatch) => swatch.handle === handleizedColor);
      // exit if no matching swatch found
      if (!matchingSwatch) {
        if(this.allowDebug()) console.log('product-card.js: setSelectedSwatchData(), no matching swatch found', swatchElement);
        // reset the swatch data
        matchingSwatch = {};
        swatchElement = null
      }
      // set the selected swatch dataset
      this.selectedSwatchData = matchingSwatch;
      // set the selected swatch element
      this.selectedSwatchElement = swatchElement;
    }

    /**
     * Updates the product images based on the selected swatch
     * Creates a slider if there are multiple images and it's not disabled
     * @param {Object} swatch - The swatch data object
     */
    setProductImagesFromSwatch(swatch){
      if(this.allowDebug()) console.log('product-card.js: setProductImagesFromSwatch(), setting product images from swatch', swatch);
      // early exit conditions
      if(!swatch) return;
      // get the media wrapper
      const cardMediaContainer = this.querySelector(".card__media");
      if(!cardMediaContainer) return;
      // if we have a slider, destroy it.
      this.destroySwatchMediaSlider();
      // clear the main media wrapper
      cardMediaContainer.replaceChildren();
      // create the card media link
      const cardMediaLink = createElementFromHTML(`
        <a
          id="product-card-media-link-${this.product.handle}"
          href="${swatch.url}"
          class="media media--transparent"
          aria-labelledby="product-card-media-link-${this.product.handle} Badge-${this.dataset.sectionId}-${this.product.id}"
        ></a>
      `);
      cardMediaContainer.appendChild(cardMediaLink);

      // get the swatch images
      // swatches images is an array of HTML image tag strings.
      const swatchImages = swatch.media;

      if (swatchImages.length > 0) {
        // First, always populate the initial media container with 1-2 images
        const firstImageHTML = swatchImages[0];
        cardMediaLink.appendChild(createElementFromHTML(firstImageHTML));

        // Add second image if available and secondary images are enabled,
        // and the product has more than 2 images. If the product has more than 2 images,
        // the slider will be shown instead of the secondary image.
        if(swatchImages.length === 2){
          cardMediaLink.classList.add('media--hover-effect');
          cardMediaLink.appendChild(createElementFromHTML(swatchImages[1]));
        }
        // the slider is a secondary sibling element of the card media link. This element is only present if the product has more than 2 images, and the user is mouseovering the product card.
        this.generateSwatchMediaSlider(swatch);
      } else {
        // Fallback to default product media if no swatch images
        // add the first image to the card media link, if we are not lazy loading the component eager load the first image.
        const defaultImageHTML = this.product.media[0];
        // append the default image to the card media link
        cardMediaLink.appendChild(createElementFromHTML(defaultImageHTML));
      }
    }

    generateSwatchMediaSlider(swatch = null){
      if(this.allowDebug()) console.log('product-card.js: generateSwatchMediaSlider()', this);
      if(this.disableGallery) return;
      if(!swatch) return;
      // get the card media container
      const cardMediaContainer = this.querySelector(".card__media");
      if(!cardMediaContainer) return;
      // get the swatch images
      const swatchImages = swatch.media;
      if(!swatchImages) return;
      // Check if we need the slider
      const isMobileView = isMobile();
      let enableSlider = isMobileView ? swatchImages.length > 1 : swatchImages.length > 2;
      if(!enableSlider) return;
      const initialIndex = isMobileView ? 0 : 1;
      // Create carousel HTML with template literals
      let sliderHTML = `<div class="plp-card-carousel gradient">`;
      // Add carousel cells (limited to first 5)
      swatchImages.slice(0, 5).forEach((imageTag, index) => {
        const imageTagHTML = imageTag.replace('src=', 'data-flickity-lazyload-src=').replace('srcset=', 'data-flickity-lazyload-srcset=').replace('loading="lazy"', '');
        sliderHTML += `
          <a
            id="product-card-media-slide-${this.product.handle}-${index}"
            class="carousel-cell media"
            href="${swatch.url}"
          >
            ${imageTagHTML}
          </a>`;
      });
      sliderHTML += `</div>`;
      // Append the slider to the media wrapper (as a sibling to the initial media container)
      const sliderWrapper = createElementFromHTML(sliderHTML);
      // insert the slider into the card media container
      cardMediaContainer.appendChild(sliderWrapper);
      // Add slider classes based on mobile status
      let lazyLoad = true;
      // if we are the first grid item, add an identifier class to the slider wrapper
      if(this.isFirstGridItem()){
        // add the first-slider class to the slider wrapper for extra identification
        sliderWrapper.classList.add('first-slider');
        // if we are on a mobile device, we want to also load the next adjacent slider image.
        if(isMobileView){
          lazyLoad = 1;
        }
      }
      // Initialize Flickity
      this.slider = new Flickity(sliderWrapper, {
        cellAlign: "right",
        contain: true,
        initialIndex: initialIndex,
        lazyLoad: lazyLoad,
        wrapAround: true,
        pageDots: true,
        arrowShape: 'M 10 50 L 50 90 L 55 85 L 20 50 L 55 15 L 50 10 Z'
      });
      // Add custom pagination if needed
      if (isMobileView && swatchImages.length >= 4) {
        const flickityDots = sliderWrapper.querySelector('.flickity-page-dots');
        if (!flickityDots) return;
        flickityDots.classList.add('custom-pagination');
        // Create the dots function
        const createDots = (index = 0) => {
          // Clear existing dots
          flickityDots.replaceChildren();
          // Get slider configuration details
          const isFourSlides = swatchImages.length === 4;
          const dotsCount = index < 3 ? 3 : isFourSlides ? 1 : 2;
          const activeIndex = index < 3 ? index : isFourSlides ? 0 : index - 3;
          const barOnLeft = index >= 3;
          // Create and add pagination elements
          const dots = Array.from({ length: dotsCount }, (_, i) => {
            const dot = document.createElement('div');
            dot.classList.add('pagination-dot');
            if (i === activeIndex) dot.classList.add('active');
            return dot;
          });
          // Clear and add new dots
          flickityDots.replaceChildren(...dots);
          // Create and position bar
          const bar = document.createElement('div');
          bar.classList.add('pagination-bar');
          // Insert bar at the appropriate position
          if (barOnLeft) {
            flickityDots.insertBefore(bar, flickityDots.firstChild);
          } else {
            flickityDots.appendChild(bar);
          }
        };
        // Set up slide change event and initialize dots
        this.slider.on('change', createDots);
        createDots(0);
      }
    }

    destroySwatchMediaSlider(){
      if(this.allowDebug()) console.log('product-card.js: destroySwatchMediaSlider()', this);
      if(!this.slider) return;
      // get the card media container
      const cardMediaContainer = this.querySelector(".card__media");
      if(!cardMediaContainer) return;
      // destroy the slider
      this.slider.destroy();
      this.slider = null;
      // remove the slider wrapper
      const sliderWrapper = cardMediaContainer.querySelector('.plp-card-carousel');
      if(sliderWrapper){
        cardMediaContainer.removeChild(sliderWrapper);
      }
    }

    /**
     * Handles swatch change events
     * Updates images, price, and variant data based on the new swatch
     * @param {CustomEvent} event - The swatch change event
     * @returns {Promise<void>}
     */
    async handleSwatchChange(event){
      // update card urls
      this.setProductLinks(`${this.selectedSwatchData.url}`);

      // update media
      this.setProductImagesFromSwatch(this.selectedSwatchData);

      // update price element to the swatch price
      if(!this.showSoldOutState) {
        // update the price element
        this.setPriceElement(createElementFromHTML(this.selectedSwatchData?.price_html));
      }

      // update variant based on the swatch, use this to update promotions.
      const variantFromSwatch = this.findVariantById(this.selectedSwatchData.variant);
      if(variantFromSwatch){
        // update the variant
        this.variant = variantFromSwatch;
        // update promotion variables
        await this.updatePromotionVariables(this.variant);
      }

      // handle the quick add to bag
      const quickAddToBag = this.querySelector('quick-add-to-bag');
      if(quickAddToBag){
        if(quickAddToBag.isActive()) {
          // inject the product options
          await quickAddToBag.injectProductOptions();
        } else {
          // open the quick add to bag
          quickAddToBag.open();
        }
      }
    }

    /**
     * Handles non-swatch product option clicks
     * Updates selected options and finds matching variant
     * @param {HTMLElement} option - The option input element that was clicked
     * @returns {Promise<void>}
     */
    async handleProductOptionClick(option){
      // early exit conditions
      if(!option) {
        if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), no option', option);
        return;
      }
      if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), option', option);
      // get the quick add to bag
      const quickAddToBag = this.querySelector('quick-add-to-bag');
      // exit if no quick add to bag or the quick add to bag is not active
      if(!quickAddToBag || (quickAddToBag && !quickAddToBag.isActive())) {
        if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), no quick add to bag or the quick add to bag is not active', quickAddToBag);
        return;
      }
      // clear the cart message
      quickAddToBag.clearCartMessage();
      // update the product options availability
      quickAddToBag.updateProductOptionsAvailability();
      // get the quick add to bag options
      const quickAddToBagOptions = quickAddToBag.querySelectorAll('.product-card__option');
      // exit if the quick add to bag options length is 0
      // If the quick add to bag shows is NOT rendering options, move on, that is a special view with it's own handlers.
      if(quickAddToBagOptions.length === 0) {
        if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), no quick add to bag options', quickAddToBagOptions);
        return;
      }
      // At this point we have an active quick add to bag UI open, with options to select.
      const optionGroups = [...this.querySelectorAll('.product-card__option')];
      // get the selected options
      const selectedOptions = [...this.querySelectorAll('.product__option-input:checked')];
      // get the option groups count
      const optionGroupsCount = optionGroups.length;
      // get the selected options count
      const selectedOptionsCount = selectedOptions.length;
      // exit if the option groups count is not equal to the selected options count
      if(optionGroupsCount === selectedOptionsCount){
        /*
          When all options are selected, find a variant made up of the selected options in this.product.variants.
          Every entry in selectedOptions is an input:checked element.
          These input elements have data-option-key attribute which is a value like "option1" "option2" "option3" etc.
          These input elements also have a value attribute, which is a value that represents the value of "option1" "option2" etc of a variant in this.product.variants
          Find a variant in this.product.variants where the option keys match the related option value.
          Update product card UI with select variant information.
        */
        if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), all option groups have a value', optionGroups, selectedOptions);
        const selectedVariant = this.findMatchingVariant(selectedOptions);
        // exit if no selected variant
        if (!selectedVariant) {
          if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), no selected variant', selectedVariant);
          return;
        }
        // update the variant
        this.variant = selectedVariant;
        // update promotion variables
        await this.updatePromotionVariables(this.variant);
        // update product card links and price elements
        this.updateVariantUI(this.variant);
        // per client's request, when we have a resolved variant, add it to cart.
        if(this.atcOnVariantResolve){
          await this.handleAddToCart();
        }
      } else {
        if(this.allowDebug()) console.log('product-card.js: handleProductOptionClick(), not all option groups have a value', optionGroups, selectedOptions);
        // Not all option groups have a value, do nothing at this point.
        return;
      }
    }

    /**
     * Finds a variant by its ID
     * @param {string|number} id - The variant ID to find
     * @returns {Object|null} The matching variant or null if not found
     */
    findVariantById(id) {
      // early exit conditions
      if(!id) return null;
      // exit if no product variants
      if(!this.product.variants) return null;
      // return the variant or null if not found
      return this.product.variants.find((variant) => variant.id === id) || null;
    }

    /**
     * Finds a variant that matches the selected options
     * @param {Object} selectedOptions - Map of option keys to selected values
     * @returns {Object|null} The matching variant or null if not found
     */
    findMatchingVariant(selectedOptions) {
      // early exit conditions
      if(!selectedOptions) return;
      // return the variant
      return this.product.variants.find((variant) => {
        return selectedOptions.every((option) => {
          const optionIndex = option.dataset.optionKey;
          const optionValue = option.value;
          return variant[optionIndex] === optionValue;
        });
      });
    }

    /**
     * Updates the UI elements based on the selected variant
     * @param {Object} variant - The selected variant
     */
    updateVariantUI(variant){
      // early exit conditions
      if(!variant) return;
      // update the product links
      this.setProductLinks(variant.url);
      // update the price element
      if(!this.showSoldOutState) {
        this.setPriceElement(createElementFromHTML(variant.price_html));
      }
    }

    /**
     * Handles product link clicks for Nosto analytics
     * Records search click events when users click on product links from Nosto search/category results
     * Uses dual attribution: recordSearchClick for search-specific tracking AND recordAttribution for generic Nosto events
     * @see https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
     * @see https://docs.nosto.com/techdocs/apis/frontend/js-apis/record-attribution
     */
    handleNostoProductClick() {
      // Fire-and-forget - don't block navigation
      try {
        const context = this.dataset.nostoContext || "product-card";

        // Map context to Nosto search type
        let searchType = "serp"; // Default fallback
        if (context.includes("collection") || context.includes("category")) {
          searchType = "category";
        } else if (context.includes("predictive") || context.includes("autocomplete")) {
          searchType = "autocomplete";
        } else if (context.includes("search") || context.includes("serp")) {
          searchType = "serp";
        }

        // Determine refType for recordAttribution: "cmp" for category merchandising, "os" for onsite search
        const refType = searchType === "category" ? "cmp" : "os";

        // Get product ID - use dataset.productId as fallback for client-side rendered cards (e.g., PredictiveSearch)
        // where this.product may not be loaded yet due to async fetchProduct()
        const productId = this.product?.id || this.dataset.productId;
        if (!productId) {
          if (this.allowDebug()) {
            console.warn('Nosto click tracking: No product ID available');
          }
          return;
        }

        // Build hit object required by recordSearchClick
        // Use dataset.fetchUrl as primary source, falling back to this.product.url
        const hit = {
          productId: String(productId),
          url: this.dataset.fetchUrl?.split('?')[0] || this.product?.url || ''
        };

        // Get variant ID for targetFragment (SKU-level tracking)
        // Use current variant ID, fallback to default variant ID from dataset
        const variantId = this.variant?.id || this.defaultVariantId;

        if (this.allowDebug()) {
          console.log('Nosto click tracking:', { searchType, refType, hit, context, variantId });
        }

        // Import and call asynchronously without awaiting (fire-and-forget)
        // This ensures we don't block the navigation
        // DUAL ATTRIBUTION: Call both recordSearchClick AND recordAttribution to couple search events to generic Nosto events
        import('@utilities/nosto').then(({ nostoRecordSearchClick, nostoRecordAttribution }) => {
          // 1. Search-specific tracking
          nostoRecordSearchClick(searchType, hit, { debug: this.allowDebug() });

          // 2. Generic attribution to couple search events to Nosto events for accurate dashboard reporting
          const attributionParams = {
            type: "vp", // View Product
            target: String(productId),
            refType: refType,
            debug: this.allowDebug()
          };
          if (variantId) {
            attributionParams.targetFragment = String(variantId);
          }
          nostoRecordAttribution(attributionParams);
        }).catch((error) => {
          if (this.allowDebug()) {
            console.error('Nosto search click tracking error:', error);
          }
        });
      } catch (error) {
        // Don't block navigation on tracking errors
        if (this.allowDebug()) {
          console.error('Nosto search click tracking error:', error);
        }
      }
    }

    /**
     * Handles add to cart button clicks
     * @param {HTMLElement} button - The clicked button
     * @returns {Promise<void>}
     */
    async handleAddToCartClick(button){
      // early exit conditions
      if(!button) return;
      // get the parent quick add to bag
      const parentQuickAddToBag = button.closest('quick-add-to-bag');
      // exit if no parent quick add to bag
      if(!parentQuickAddToBag) return;
      // get the selected swatch data
      const selectedSwatchData = this.getSelectedSwatchData();
      // exit if no selected swatch data
      if(!selectedSwatchData) return;
      // get the full variant data based off the swatch ID
      this.variant = this.findVariantById(this.selectedSwatchData.variant);
      // exit if no variant
      if(!this.variant) return;
      // handle add to cart
      await this.handleAddToCart();
    }

    /**
     * Adds the current product variant to the cart
     * Handles different product types (standard, parent, bundle, etc.)
     * @returns {Promise<void>}
     */

    async handleAddToCart() {
      // early exit conditions
      if (!Object.keys(this.variant).length > 0) return;

      try {
        // get the atc buttons
        const atcButtons = this.querySelectorAll('.product-card__add-to-bag');

        // set loading state
        this.setAddToCartLoadingState(atcButtons, true);

        // compile the item data being sent to cart
        let properties = this.collectLineItemProperties();

        const item = {
          id: this.variant.id,
          quantity: 1,
          properties: properties
        };

        // Use standardized cart API
        const { addToCart } = await import('@utilities/theme');
        await addToCart(item, { openDrawer: true });

        // Record Nosto search add-to-cart if product came from Nosto search/category data
        // Uses dual attribution: recordSearchAddToCart for search-specific tracking AND recordAttribution for generic Nosto events
        // See: https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
        // See: https://docs.nosto.com/techdocs/apis/frontend/js-apis/record-attribution
        if (this.dataset.vendor === "nosto") {
          try {
            const { nostoRecordSearchAddToCart, nostoRecordAttribution } = await import('@utilities/nosto');
            const context = this.dataset.nostoContext || "product-card";

            // Map context to Nosto search type
            let searchType = "serp"; // Default fallback
            if (context.includes("collection") || context.includes("category")) {
              searchType = "category";
            } else if (context.includes("predictive") || context.includes("autocomplete")) {
              searchType = "autocomplete";
            } else if (context.includes("search") || context.includes("serp")) {
              searchType = "serp";
            }

            // Determine refType for recordAttribution: "cmp" for category merchandising, "os" for onsite search
            const refType = searchType === "category" ? "cmp" : "os";

            // Get product ID - use dataset.productId as fallback for client-side rendered cards
            const productId = this.product?.id || this.dataset.productId;
            if (!productId) {
              if (this.allowDebug()) {
                console.warn('Nosto ATC tracking: No product ID available');
              }
            } else {
              // Build hit object required by recordSearchAddToCart
              const hit = {
                productId: String(productId),
                url: this.dataset.fetchUrl?.split('?')[0] || this.product?.url || ''
              };

              if (this.allowDebug()) {
                console.log('Nosto ATC tracking:', { searchType, refType, hit, context });
              }

              // DUAL ATTRIBUTION: Call both recordSearchAddToCart AND recordAttribution to couple search events to generic Nosto events
              // 1. Search-specific tracking
              await nostoRecordSearchAddToCart(searchType, hit, { debug: this.allowDebug() });

              // 2. Generic attribution to couple search events to Nosto events for accurate dashboard reporting
              await nostoRecordAttribution({
                type: "cp", // Add to Cart (Cart Product)
                target: String(productId),
                refType: refType,
                debug: this.allowDebug()
              });
            }
          } catch (error) {
            // Attribution failures should not affect cart operation
            if (this.allowDebug()) {
              console.error('Nosto search add-to-cart tracking error:', error);
            }
          }
        } else {
          if (this.allowDebug()) {
            console.log('ProductCard ATC - Skipping Nosto tracking (vendor is not "nosto")');
          }
        }

        // reset loading state
        this.setAddToCartLoadingState(atcButtons, false);

        // close the active quick add to bag and clear selections
        const activeQuickAddToBag = this.querySelector('quick-add-to-bag.active');
        if (activeQuickAddToBag) {
          activeQuickAddToBag.close();
          activeQuickAddToBag.clearSelections();
        }

      } catch (error) {
        console.error('Product Card: Error adding to cart:', error);

        // reset loading state on error
        const atcButtons = this.querySelectorAll('.product-card__add-to-bag');
        this.setAddToCartLoadingState(atcButtons, false);

        // show error message
        const activeQuickAddToBag = this.querySelector('quick-add-to-bag.active');
        if (activeQuickAddToBag) {
          activeQuickAddToBag.setCartMessage(error.message || 'Error adding to cart');
        }
      }
    }

    /**
     * Collect line item properties from promotion and hidden inputs
     * @returns {Object} Properties object
     */
    collectLineItemProperties() {
      let properties = {};

      // Collect promotion properties
      if (Object.keys(this.promotion).length > 0) {
        const promotionProperties = window.theme?.utilities?.getPromotionProperties(this.promotion);
        if (Object.keys(promotionProperties).length > 0) {
          Object.entries(promotionProperties).forEach(([key, value]) => {
            properties[`${key}`] = value.value;
          });
        }
      }

      // find other properties by looking for hidden properties inputs
      this.querySelectorAll(`input[type="hidden"][name^="properties["]`).forEach(el => {
        const propertyName = el.name.match(/\[([^)]+)\]/)?.[1];
        if (propertyName) properties[propertyName] = el.value;
      });

      // set the order type
      properties["_ord_type"] = this.product?.order_type ?? "WHP";

      // set the minicart message
      if ((this.product.hasOwnProperty("show_minicart_message") && this.product.show_minicart_message === true) &&
          (this.variant.hasOwnProperty("minicart_message") && this.variant.minicart_message.length > 0)) {
        properties["_minicart_message"] = this.variant.minicart_message;
      }

      // set the preorder message
      if ((this.product.hasOwnProperty("show_preorder_message") && this.product.show_preorder_message === true) &&
          (this.variant.hasOwnProperty("preorder_message") && this.variant.preorder_message.length > 0)) {
        properties["_po_message"] = this.variant.preorder_message;
      }

      // set the final sale message
      if (this.variant.hasOwnProperty("is_final_sale") && this.variant.is_final_sale === true) {
        properties["_Final Sale"] = true;
      }

      // set the color id
      if (this.variant.hasOwnProperty("color")) {
        properties["_color_id"] = this.variant.color;
      }

      // set the intuitive cid
      if (window.theme?.customer?.id) {
        properties["_intuitive_cid"] = window.theme.customer.id;
      }

      properties["_add_to_cart_component"] = "product-card";

      return properties;
    }

    /**
     * Set loading state for add to cart buttons
     * @param {NodeList} buttons - The buttons to update
     * @param {boolean} isLoading - Whether to show loading state
     */
    setAddToCartLoadingState(buttons, isLoading) {
      if (!buttons) return;

      buttons.forEach((button) => {
        if (isLoading) {
          button.setAttribute('aria-disabled', true);
          button.classList.add('loading');
          button.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
        } else {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          button.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        }
      });
    }

    /**
     * Updates all product links with a new URL
     * @param {string} url - The new URL to set
     */
    setProductLinks(url){
      // early exit conditions
      if(!url) return;
      // exit if no product links
      const productLinks = this.querySelectorAll('a');
      if(!productLinks) return;
      // set the product links
      productLinks.forEach((productLink)=>{
        productLink.href = url;
      });
      // get the quickview link
      const quickviewLink = this.querySelector('.quick-add__submit');
      // set the product url
      if(quickviewLink) quickviewLink.dataset.productUrl = url;
    }

    /**
     * Updates the price element with new HTML
     * @param {string} html - The HTML to set for the price
     */
    setPriceElement(html){
      // early exit conditions
      if(!html) return;
      // exit if no price element
      if(!this.elements.priceElement) return;
      // set the price element
      this.elements.priceElement.replaceChildren();
      // set the price element
      this.elements.priceElement.appendChild(html);
    }

    /**
     * Gets the product data
     * @returns {Object} The product data
     */
    getProduct(){
      return this.product ?? {};
    }

    /**
     * Gets the selected swatch element
     * @returns {HTMLElement|null} The selected swatch element
     */
    getSelectedSwatchElement(){
      return this.selectedSwatchElement ?? null;
    }

    /**
     * Gets the selected swatch data
     * @returns {Object|null} The selected swatch data
     */
    getSelectedSwatchData(){
      return this.selectedSwatchData ?? {};
    }

    /**
     * Gets the HTML for the price range
     * @returns {string} The price range HTML
     */
    getPriceRangeHTML(){
      return this.product.price_range_html ?? null;
    }

    /**
     * Gets the variants for the product
     * @returns {Array} Array of variant objects
     */
    getVariants(){
      return this.product.variants ?? [];
    }

    /**
     * Gets the bundle products if this is a bundle product
     * @returns {Array} Array of bundle product objects
     */
    getBundleProducts(){
      return this.product.bundle_products ?? [];
    }

    /**
     * Gets the current variant
     * @returns {Object|null} The current variant
     */
    getVariant(){
      return this.variant ?? {};
    }

    /**
     * Checks if the product has color swatches
     * @returns {boolean} Whether the product has swatches
     */
    hasSwatches() {
      return this.product.swatches?.length > 0;
    }

    /**
     * Gets the swatch data for the product
     * @returns {Array} Array of swatch objects
     */
    getSwatchesData() {
      return this.product.swatches;
    }

    /**
     * Checks if this is a standard product (not complex or bundle)
     * @returns {boolean} Whether this is a standard product
     */
    isStandardProduct(){
      return this.product.is_standard_product ?? false;
    }

    /**
     * Checks if this is a parent product in a complex product
     * @returns {boolean} Whether this is a parent product
     */
    isParentProduct(){
      return this.product.is_parent_product ?? false;
    }

    /**
     * Checks if this is a bundle product
     * @returns {boolean} Whether this is a bundle product
     */
    isBundleProduct(){
      return this.product.is_bundle_product ?? false;
    }

    /**
     * Checks if this is a single variant product
     * @returns {boolean} Whether this is a single variant product
     */
    isSingleVariantProduct(){
      return this.product.is_single_variant_product ?? false;
    }

    /**
     * Checks if this is the first grid item
     * @returns {boolean} Whether this is the first grid item
     */
    isFirstGridItem(){
      // get the grid item containing the product card
      const closestGridItem = this.closest(".grid__item.grid__item--product");
      // exit if no grid item
      if(!closestGridItem) return false;
      // get the first page product grid
      const gridItemContainer = closestGridItem.closest(`[data-products-container][data-page="1"]`);
      // exit if no first page product grid
      if(!gridItemContainer) return false
      // get the first grid item of the first page product grid
      const firstGridItem = gridItemContainer.querySelector(".grid__item.grid__item--product:first-of-type");
      // exit if no first grid item, which would be odd in this context at this point
      if(!firstGridItem) return false;
      const firstGridItemCard = firstGridItem.querySelector("product-card");
      if(!firstGridItemCard) return false;
      // return true if the first grid item id matches the current grid item id
      return firstGridItemCard.id === this.id;
    }

    /**
     * Lifecycle callback when the element is removed from the DOM
     * Clean up any remaining references
     */
    disconnectedCallback() {
      // Clean up any remaining references
      this.product = null;
      this.variant = null;
    }



    /**
     * Checks if a variant is fully available (both shopify and greyson availability)
     * @param {Object} variant - The variant to check
     * @returns {boolean} True if variant is fully available
     */
    isVariantFullyAvailable(variant) {
      return variant.available_shopify && variant.available_greyson;
    }

    /**
     * Checks if a variant is at least shopify available
     * @param {Object} variant - The variant to check
     * @returns {boolean} True if variant is shopify available
     */
    isVariantShopifyAvailable(variant) {
      return variant.available_shopify;
    }

    /**
     * Updates the selected swatch to match the given variant
     * @param {Object} variant - The variant to match swatch selection to
     */
    updateSwatchFromVariant(variant) {
      if (!variant || !this.hasSwatches()) {
        return;
      }

      // Find the swatch that matches the variant's color
      const targetSwatch = this.product.swatches?.find(swatch =>
        swatch.variant === variant.id || swatch.color === variant.color
      );

      if (targetSwatch) {
        // Update selected swatch data
        this.selectedSwatchData = targetSwatch;

        // Update swatch UI selection
        const swatchInputs = this.querySelectorAll('.product__option-swatch');
        swatchInputs.forEach(input => {
          const isTargetSwatch = input.dataset.color === targetSwatch.handle ||
                                input.value === targetSwatch.color;
          input.checked = isTargetSwatch;

          // Update label classes
          const label = input.closest('label') || this.querySelector(`label[for="${input.id}"]`);
          if (label) {
            if (isTargetSwatch) {
              label.classList.add('active');
            } else {
              label.classList.remove('active');
            }
          }
        });

        // Update selected swatch element reference
        this.selectedSwatchElement = this.querySelector('.product__option-swatch:checked');

        // Update product images from the new swatch
        this.setProductImagesFromSwatch(targetSwatch);

        if(this.allowDebug()) {
          console.log('product-card.js: updateSwatchFromVariant() - Updated swatch to match variant', targetSwatch);
        }
      }
    }

  }

  customElements.define('product-card', ProductCard);
}
